import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../utils/security";
import { getProjectsBaseUrl } from "../../../config/config";

// Putting a file into a project: the projects to choose from, and the upload. Outlook
// files emails through it; the document hosts' Save to Jylo uploads the open document.
//
// The upload is the same three calls the web client makes, in the same order -- open
// an upload record, post the file against it, close it -- because closing it is what
// tells the project's members that documents arrived. Posting the file alone would
// work and notify nobody.
//
// No processing settings are sent. The API resolves OCR and citation settings from the
// organisation's own configuration on every upload and ignores what a client asks for
// (DocumentController.Create in `projects`), so there is nothing here to get wrong.

export interface ProjectSummary {
  objectIdentifier: string;
  name: string;
  description?: string | null;
  dateAdded: string;
  isPersonal?: boolean;
}

interface PaginationList<T> {
  items: T[];
  totalCount: number;
}

interface DocumentUploadRecord {
  objectIdentifier: string;
}

export interface FiledDocument {
  objectIdentifier: string;
  name: string;
}

/** What the suggestion model chooses among: the user's hundred newest member projects. */
export const FILING_PROJECT_COUNT = 100;

/** One page of the searchable project list. */
export const PROJECT_SEARCH_PAGE_SIZE = 25;

export interface ProjectSearchPage {
  items: ProjectSummary[];
  totalCount: number;
}

/**
 * The largest file posted in one request. Above it the web client switches to chunked
 * upload, which this does not implement; see docs/assumptions.md A-46.
 */
export const MAX_SINGLE_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * The page after `lastPageParam`, or undefined once everything has been loaded. Stops on
 * an empty page too, so a total that overstates what the server will return cannot page
 * for ever.
 */
export function nextProjectPage(
  lastPage: ProjectSearchPage,
  allPages: ProjectSearchPage[],
  lastPageParam: number
): number | undefined {
  const loaded = allPages.reduce((count, page) => count + page.items.length, 0);
  return lastPage.items.length > 0 && loaded < lastPage.totalCount ? lastPageParam + 1 : undefined;
}

export const filingApi = createApi({
  reducerPath: "filingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "",
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // "Newest" means most recently created: the API records no last-opened time for a
    // project, so there is no truer "recent" to ask for.
    getFilingProjects: builder.query<ProjectSummary[], void>({
      query: () =>
        `${getProjectsBaseUrl()}/projects?scope=member&sorting=dateAdded&desc=true` +
        `&pageNumber=1&pageSize=${FILING_PROJECT_COUNT}`,
      // A reply without `items` is an API we do not understand, not an empty list. Mapping
      // it to [] would tell the user they have no projects, which is the one wrong answer
      // that looks like a right one.
      transformResponse: (response: PaginationList<ProjectSummary>) => {
        if (!Array.isArray(response?.items)) {
          throw new Error("Jylo returned the project list in a form this version cannot read.");
        }
        return response.items;
      },
    }),

    // Every project the user is a member of, searched on the server (name and description)
    // and a page at a time, so a project is reachable however old it is. Newest first, as
    // the suggestion's list is: a matter in hand this week is likelier than a better match.
    searchProjects: builder.infiniteQuery<ProjectSearchPage, { searchText: string }, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: nextProjectPage,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `${getProjectsBaseUrl()}/projects`,
        params: {
          scope: "member",
          searchText: queryArg.searchText,
          sorting: "dateAdded",
          desc: true,
          pageNumber: pageParam,
          pageSize: PROJECT_SEARCH_PAGE_SIZE,
        },
      }),
      // As above: a reply without `items` is not an empty list.
      transformResponse: (response: PaginationList<ProjectSummary>) => {
        if (!Array.isArray(response?.items)) {
          throw new Error("Jylo returned the project list in a form this version cannot read.");
        }
        return { items: response.items, totalCount: response.totalCount ?? response.items.length };
      },
    }),

    uploadDocument: builder.mutation<
      FiledDocument,
      {
        projectObjectIdentifier: string;
        file: Blob;
        fileName: string;
        metadata: Record<string, string>;
        /** The upload record's name, which the "documents added" notification shows. */
        uploadName: string;
      }
    >({
      // Three requests, so a queryFn rather than a query. A failure after the record is
      // opened leaves it open and empty -- which is exactly what the web client leaves
      // when an upload fails, and harmless: it holds no documents and notifies no one.
      async queryFn(
        { projectObjectIdentifier, file, fileName, metadata, uploadName },
        _api,
        _extra,
        fetchWithBQ
      ) {
        const base = `${getProjectsBaseUrl()}/projects/${projectObjectIdentifier}`;
        const session = { name: uploadName, fileCount: 1 };

        const opened = await fetchWithBQ({
          url: `${base}/document-uploads`,
          method: "POST",
          body: session,
        });
        if (opened.error) return { error: opened.error };
        const uploadId = (opened.data as DocumentUploadRecord).objectIdentifier;

        // The file part keeps its own type (message/rfc822, the Office MIME types), which
        // is what the API stores as the document's type; the request itself is
        // multipart/form-data. Front Door's WAF
        // blocks on Content-Type (see saveDraftContent in checkoutApi), and whether it
        // reads a part's is A-44 in docs/assumptions.md.
        const form = new FormData();
        form.append("file", file, fileName);
        form.append("metadataJson", JSON.stringify(metadata));

        const posted = await fetchWithBQ({
          url: `${base}/documents?documentUploadIdentifier=${uploadId}&source=Local`,
          method: "POST",
          body: form,
        });
        if (posted.error) return { error: posted.error };

        const closed = await fetchWithBQ({
          url: `${base}/document-uploads/${uploadId}`,
          method: "PATCH",
          body: session,
        });
        // The file is in the project by now; failing to close the record costs only the
        // "documents added" notification. Say nothing to the user about it -- they asked
        // for the file to be uploaded, and it was -- but leave a trace for a bug report.
        if (closed.error) console.warn("Filed, but the upload record did not close", closed.error);

        return { data: posted.data as FiledDocument };
      },
    }),
  }),
});

export const {
  useGetFilingProjectsQuery,
  useSearchProjectsInfiniteQuery,
  useUploadDocumentMutation,
} = filingApi;
